/**
 * The friendly preflight surface, shared by the single-tool page and the
 * wizard: "checking…", "file matches", or the mismatch panel with the
 * first few backend errors and a hint. The caller runs the actual
 * `api.preflightTool` call and passes the state down.
 */
import { useTranslation } from "react-i18next";
import { Alert, Typography } from "@mui/material";
import { PREFLIGHT_ERRORS_SHOWN } from "../lib/config";

export type PreflightState = { ok: boolean; errors: string[] } | "checking" | null;

export function PreflightPanel({ preflight }: { preflight: PreflightState }) {
  const { t } = useTranslation();

  if (preflight === null) return null;
  if (preflight === "checking") {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t("tools.checkingFile")}
      </Typography>
    );
  }
  if (preflight.ok) {
    return (
      <Alert severity="success" sx={{ mt: 1 }}>
        {t("tools.fileOk")}
      </Alert>
    );
  }
  return (
    <Alert severity="error" sx={{ mt: 1 }}>
      <Typography variant="body2" fontWeight={600}>
        {t("tools.fileMismatch")}
      </Typography>
      {preflight.errors.slice(0, PREFLIGHT_ERRORS_SHOWN).map((e) => (
        <Typography key={e} variant="caption" display="block">
          {e}
        </Typography>
      ))}
      <Typography variant="caption" color="text.secondary">
        {t("tools.fileMismatchHint")}
      </Typography>
    </Alert>
  );
}
